import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Controller } from 'react-hook-form';

import { styled } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

import { SdmTypography } from 'components';
import { theme } from 'theme';
import DropZonePreviewImage from './styledComponents/DropZonePreviewImage';
import CustomExpandIcon from './styledComponents/CustomExpandIcon';
import SimpleRichText from './styledComponents/SimpleRichText';
import CustomSelect from './styledComponents/CustomSelect';
import CustomTextFieldComponent from './styledComponents/CustomTextFieldComponent';
import BoxContainer from './styledComponents/BoxContainer';
import translations from 'utils/translations';
import { handleDropAfterErrors } from 'utils/drupalEntities';
import LabelContainer from './additionalComponents/LabelContainer';

const AccordionSummaryContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const AccordionContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const BasicAccordeon = ({
  setValue,
  control,
  getValues,
  watch,
  setCustomErrors,
  basicData,
  isBrand,
  brand,
  productLangCode,
}) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  // States
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [components, setComponents] = useState(false);
  // Constants
  const titleColor = isAccordionOpen
    ? theme.colors.primary.red
    : theme.colors.primary.black;
  const iconColor = isAccordionOpen ? 'primaryOpen' : 'primaryClosed';
  const isEn = productLangCode === 'en';
  const isNotTranslatable = (key) => isEn && !components?.[key]?.isTranslatable;

  const isNotImgTranslatable = (key) =>
    isEn && !components?.[key]?.isImgTranslatable;
  const isNotAltTranslatable = (key) =>
    isEn && !components?.[key]?.isAltTranslatable;

  // Effects
  useEffect(() => {
    if (basicData?.length) {
      const _components = {};
      basicData.map((comp) => (_components[comp.name] = comp));
      _components && setComponents(_components);
    }
  }, [basicData]);

  // Functions
  const handleChange = (acceptedFiles) => {
    const c = components.field_cover;
    handleDropAfterErrors({
      files: acceptedFiles,
      fieldName: c.name,
      maxResolution: c.maxResolution,
      maxSize: c.maxSize,
      fileExtension: c.extensions.split(' '),
      setCustomErrors,
      setValue,
      i18nErrors: i18n.errors,
    });
  };

  const renderNotTranslatableMsg = (key) =>
    isNotTranslatable(key) ? (
      <SdmTypography
        variant={'h5'}
        style={{ margin: 0, color: theme.colors.primary.red }}
      >
        {i18n.tooltip.errorTranslate}
      </SdmTypography>
    ) : null;

  return (
    <Accordion
      onChange={() => setIsAccordionOpen(!isAccordionOpen)}
      expanded={isAccordionOpen}
    >
      <AccordionSummary expandIcon={<CustomExpandIcon variant={iconColor} />}>
        <AccordionSummaryContent>
          <SdmTypography
            variant={'h3'}
            margin={0}
            style={{
              color: titleColor,
            }}
          >
            {i18n.titles.basicAccordeon}
          </SdmTypography>
          <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
            {i18n.titles.basicAccordeonSub}
          </SdmTypography>
        </AccordionSummaryContent>
      </AccordionSummary>

      <AccordionDetails>
        {components && (
          <AccordionContent>
            <BoxContainer>
              <LabelContainer
                label={`${i18n.titles[isBrand ? 'brandName' : 'productName']}`}
                isRequired={true}
                description={isBrand ? i18n.titles.brandDesc : null}
              />
              {/* TODO: sicuramente andrà rivisto */}
              <Controller
                name="title"
                control={control}
                defaultValue={isBrand && brand ? brand.brandName || '' : ''}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={
                        i18n.inputText[isBrand ? 'brandName' : 'productName']
                      }
                      placeholder={i18n.inputText.productNamePlace}
                      variant="outlined"
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={isBrand}
                    />
                  );
                }}
              />
            </BoxContainer>

            {components.field_brand && (
              <BoxContainer>
                <SdmTypography
                  variant={'h5'}
                  style={{ margin: 0, textTransform: 'uppercase' }}
                >
                  {components.field_brand.label}{' '}
                  {components.field_brand.isRequired ? (
                    <span
                      style={{
                        color: theme.colors.primary.red,
                        fontWeight: 400,
                      }}
                    >
                      {' '}
                      *
                    </span>
                  ) : (
                    ''
                  )}
                </SdmTypography>
                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {components.field_brand.description ||
                    i18n.inputText.brandDesc}
                </SdmTypography>
                {renderNotTranslatableMsg('field_brand')}
                <Controller
                  name={components.field_brand.name}
                  control={control}
                  render={(props) => {
                    return (
                      <CustomSelect
                        title={components.field_brand.label}
                        items={components.field_brand.options}
                        style={{ marginTop: '15px' }}
                        onChange={(e) => props.onChange(e.target.value)}
                        value={props.value}
                        disabled={isNotTranslatable('field_brand')}
                      />
                    );
                  }}
                />
              </BoxContainer>
            )}

            {components.body && (
              <BoxContainer>
                <SdmTypography
                  variant={'h5'}
                  style={{ margin: 0, textTransform: 'uppercase' }}
                >
                  {components.body.label}{' '}
                  {components.body.isRequired ? (
                    <span
                      style={{
                        color: theme.colors.primary.red,
                        fontWeight: 400,
                      }}
                    >
                      {' '}
                      *
                    </span>
                  ) : (
                    ''
                  )}
                </SdmTypography>
                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {components.body.description}
                </SdmTypography>
                {renderNotTranslatableMsg('body')}
                <SimpleRichText
                  control={control}
                  name={components.body.name}
                  getValues={getValues}
                  maxChar={250}
                  disabled={isNotTranslatable('body')}
                />
              </BoxContainer>
            )}

            {components.field_cover && (
              <BoxContainer>
                <SdmTypography
                  variant={'h5'}
                  style={{ margin: 0, textTransform: 'uppercase' }}
                >
                  {components.field_cover.label}
                  {components.field_cover.isRequired ? (
                    <span
                      style={{
                        color: theme.colors.primary.red,
                        fontWeight: 400,
                      }}
                    >
                      {' '}
                      *
                    </span>
                  ) : (
                    ''
                  )}
                </SdmTypography>
                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {components.field_cover.description}
                </SdmTypography>
                {renderNotTranslatableMsg('field_cover')}
                <DropZonePreviewImage
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  name={components?.field_cover?.name}
                  altName={`alt_${components?.field_cover?.name}_0`}
                  altDescription={isBrand ? i18n.text.altDescription : ''}
                  captionName={components?.field_didascalia?.name}
                  captionLabel={components?.field_didascalia?.label || ''}
                  captionDescription={
                    components?.field_didascalia?.description || ''
                  }
                  accept={{
                    'image/*': components.field_cover.extensions
                      ? components.field_cover.extensions
                          .split(' ')
                          .map((e) => `.${e}`)
                      : ['jpg'],
                  }}
                  disableImg={isNotImgTranslatable('field_cover')}
                  disableAlt={isNotAltTranslatable('field_cover')}
                  disableCaption={isNotTranslatable('field_didascalia')}
                  handleChange={handleChange}
                  isMultiple={false}
                  specs={[
                    i18n.imageSpecs.number,
                    `${i18n.imageSpecs.size} ${components.field_cover.maxSize}`,
                    `${i18n.imageSpecs.format} ${components.field_cover.extensions}`,
                    `${i18n.imageSpecs.logoSpecSize} ${components.field_cover.maxResolution}px`,
                  ]}
                  maxResolution={components.field_cover.maxResolution}
                  setCustomErrors={setCustomErrors}
                />
              </BoxContainer>
            )}
          </AccordionContent>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default BasicAccordeon;
